import React, { ReactNode } from 'react';

// --- STRICT TYPING (Discriminated Union) --- //

type BasedivProps = {
  variant: 'basic' | 'split' | 'hero';
  className?: string;
  /** Optional modifier to apply the bottom sine-wave curve */
  isCurved?: boolean;
};

interface BasicdivProps extends BasedivProps {
  variant: 'basic';
  title: string;
}

interface SplitdivProps extends BasedivProps {
  variant: 'split';
  title: string;
  /** Accepts strings or multiple <p> tags for complex descriptions */
  description: ReactNode;
  imageSrc: string;
  imageAlt?: string;
  ctaText?: string;
  ctaLink?: string;
}

interface HerodivProps extends BasedivProps {
  variant: 'hero';
  title?: string;
  videoSrc?: string;
}

export type PagedivProps = BasicdivProps | SplitdivProps | HerodivProps;
// Place this outside/above your PageHeader component
export const CurveDivider = ({ className = '' }: { className?: string }) => (
  <div
    id="curved-divider"
    className={`absolute bottom-0 left-0 w-full z-0 h-24 sm:h-32 overflow-hidden pointer-events-none ${className}`}
  >
    <svg
      viewBox="0 0 1440 320"
      className="w-full h-full absolute bottom-0"
      preserveAspectRatio="none"
      role="presentation"
      aria-hidden="true"
    >
      <path
        d="M0,160 C150,210 350,110 500,160 S850,210 1000,160 S1250,110 1440,160 V320 H0 Z"
        fill="currentColor"
      />
    </svg>
  </div>
);

// --- COMPONENT IMPLEMENTATION --- //

// --- COMPONENT IMPLEMENTATION --- //

export const PageHeader: React.FC<PagedivProps> = (props) => {
  switch (props.variant) {
    case 'basic':
      return (
        <div
          className={`relative w-full ${props.className || 'bg-white/50'} ${
            props.isCurved
              ? 'overflow-hidden pb-16 sm:pb-24'
              : 'border-b border-brand-frost'
          }`}
        >
          <div className="relative z-10 py-10 px-6 md:px-10">
            <h1 className="premium-text text-3xl text-brand-truffle font-bold">
              {props.title}
            </h1>
          </div>

          {props.isCurved && <CurveDivider />}
        </div>
      );

    case 'split':
      return (
        <div
          className={`relative w-full ${props.className || ''} ${
            props.isCurved ? 'overflow-hidden' : ''
          }`}
        >
          <div
            className={`relative z-10 py-10 px-4 sm:py-12 sm:px-8 max-w-screen-2xl mx-auto text-center grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 text-left items-center ${
              props.isCurved ? 'pb-32 sm:pb-40' : 'pb-24'
            }`}
          >
            {/* LEFT CELL: Text & Headings */}
            <div className="flex flex-col justify-center">
              <h1 className="premium-text text-4xl sm:text-5xl font-bold text-brand-truffle">
                {props.title}
              </h1>
              <div className="mt-4 text-brand-truffle/80 text-lg leading-relaxed">
                {props.description}
              </div>

              {props.ctaText && props.ctaLink && (
                <div className="mt-8 flex items-center justify-center lg:justify-start">
                  <a
                    href={props.ctaLink}
                    className="inline-flex items-center rounded-full py-3 px-8 bg-brand-truffle text-brand-gold font-bold text-lg hover:bg-brand-gold hover:text-brand-truffle transition-colors artisan-card"
                  >
                    {props.ctaText}
                  </a>
                </div>
              )}
            </div>

            {/* RIGHT CELL: Centered Image */}
            <div className="mt-12 lg:mt-0 flex justify-center items-center w-full">
              <img
                src={props.imageSrc}
                alt={props.imageAlt || props.title}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full max-w-md sm:max-w-lg lg:max-w-xl object-contain drop-shadow-2xl"
              />
            </div>
          </div>

          {props.isCurved && <CurveDivider />}
        </div>
      );

    case 'hero':
      return (
        <div
          className={`relative w-full h-screen overflow-hidden bg-brand-truffle ${props.className || ''}`}
        >
          {props.videoSrc && (
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover opacity-60 z-0"
            >
              <source src={props.videoSrc} type="video/mp4" />
            </video>
          )}
          {props.title && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <h1 className="premium-text text-white text-5xl md:text-7xl font-bold drop-shadow-lg text-center px-4">
                {props.title}
              </h1>
            </div>
          )}

          {/* You can even allow the hero to be curved at the bottom! */}
          {props.isCurved && <CurveDivider />}
        </div>
      );

    default:
      return null;
  }
};
